import { AxiosResponse } from 'axios';

import { ASSET_TOKEN_STATUS, ASSET_TOKEN_TYPE } from '../../types';

import {
  Assets,
  AssetsTokenInfoResponse,
  Loans,
  OperationsForConfirmation,
} from './dto/Assets';
import BaseApi from './BaseApi';

interface IGeneralParams {
  token: string;
  id: number;
  dateFrom?: string;
  dateTo?: string;
}
interface IAssetsParams extends IGeneralParams {
  type: string;
  status?: string;
  issuer?: boolean;
  search?: string;
}

interface IIssueAssetsConfirm {
  token: string;
  decId: number;
}

interface IOperations {
  type: string;
}

interface IConfirmOperations {
  operationId: number;
  type: string;
}

interface ILoansParams extends IGeneralParams {
  status?: string;
}

const assetsApi = {
  getAssets({
    token,
    id,
    type,
    issuer,
    status,
    dateFrom,
    dateTo,
    search,
  }: IAssetsParams): Promise<AxiosResponse<Assets>> {
    const externalParams = {} as Record<string, any>;
    if (dateFrom && dateTo) {
      externalParams.dateFrom = dateFrom;
      externalParams.dateTo = dateTo;
    }
    const payload = {
      accountId: id,
      type,
      forIssuer: issuer,
      tokenStatus: status,
      search,
      ...externalParams,
    };
    return BaseApi.post(`api/v1/assets/search`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
  getNonIssuedAssets(): Promise<AxiosResponse<Assets>> {
    return BaseApi.get(`api/v1/assets/by/operator`);
  },
  getOperationForConfirmations({
    type,
  }: IOperations): Promise<AxiosResponse<OperationsForConfirmation>> {
    if (type) {
      return BaseApi.get(`api/v1/assets/by/operator/operations`);
      // return BaseApi.get(`api/v1/assets/by/operator/operations&type=${type}`);
    } else {
      return BaseApi.get(`api/v1/assets/by/operator/operations`);
    }
  },
  confirmIssueByDecisionId({ token, decId }: IIssueAssetsConfirm) {
    const payload = {
      decisionId: decId,
    };
    return BaseApi.post(`api/v1/issues/confirm`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
  confirmOperation({ type, operationId }: IConfirmOperations) {
    const payload = {
      operationId: operationId,
      type: type,
    };
    return BaseApi.post(
      `api/v1/assets/by/operator/operations/confirm`,
      payload
    );
  },
  getLoans({
    token,
    id,
    status,
    dateFrom,
    dateTo,
  }: ILoansParams): Promise<AxiosResponse<Loans>> {
    const externalParams = {} as Record<string, any>;
    if (dateFrom && dateTo) {
      externalParams.dateFrom = dateFrom;
      externalParams.dateTo = dateTo;
    }
    if (status) {
      externalParams.status = status;
    }
    const payload = {
      accountId: id,
      ...externalParams,
    };
    return BaseApi.post(`api/v1/credits/search`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
  getTypeAssets(
    token: string,
    payload: {
      accountId: number;
      type: ASSET_TOKEN_TYPE;
      status: ASSET_TOKEN_STATUS;
    }
  ): Promise<AxiosResponse<AssetsTokenInfoResponse[]>> {
    return BaseApi.get(
      `/api/v1/assets?accountId=${payload.accountId}&type=${payload.type}&status=${payload.status}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
  getDetailedAssetInfo(
    token: string,
    payload: {
      accountId: number;
      tokenId: string;
    }
  ): Promise<AxiosResponse<any>> {
    const { tokenId, accountId } = payload;
    return BaseApi.get(
      `/api/v1/assets/details/${tokenId}?accountId=${accountId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
};

export default assetsApi;
